import { useCallback, useRef } from 'react';
import { useOneButtonStore, type SavedAIDesign, type TreeCutSubScenario } from '../../../../../store/oneButtonStore';

/** DC system size (kW) from module count × module power — matches AI design cards. */
export function computeSystemKwFromModules(modules: number | undefined, modulePowerW: number): number {
    if (modules == null || !Number.isFinite(modules) || modules <= 0) return 0;
    if (!Number.isFinite(modulePowerW) || modulePowerW <= 0) return 0;
    return Number(((modules * modulePowerW) / 1000).toFixed(2));
}

function resolveWishlistSystemKw(
    modules: number,
    modulePowerW: number,
    systemSizeKwOverride?: number | null,
): number {
    if (typeof systemSizeKwOverride === 'number' && Number.isFinite(systemSizeKwOverride) && systemSizeKwOverride > 0) {
        return Number(systemSizeKwOverride.toFixed(2));
    }
    return computeSystemKwFromModules(modules, modulePowerW);
}

interface UseDashboardDesignHandlersArgs {
    savedAIDesigns: SavedAIDesign[];
    removeAIDesign: (aiDesignId: string) => void;
    roofCombinationsFromAi: any[];
    bestRoofsForLayout: any;
    layoutProductionByKey: Record<string, number>;
    layoutModulesByKey: Record<string, number>;
    treeScenariosByLayoutKey: Record<string, any[]>;
    treeCount: number;
    layoutSnapshotsByKey: Record<string, string | undefined>;
    addAIDesign: (design: SavedAIDesign) => void;
    addTreeCutSubScenario: (parentDesignId: string, subScenario: TreeCutSubScenario) => void;
    effectiveTreeLayoutKey: string;
    modulePowerW: number;
}

export function useDashboardDesignHandlers({
    savedAIDesigns,
    removeAIDesign,
    roofCombinationsFromAi,
    bestRoofsForLayout,
    layoutProductionByKey,
    layoutModulesByKey,
    treeScenariosByLayoutKey,
    treeCount,
    layoutSnapshotsByKey,
    addAIDesign,
    addTreeCutSubScenario,
    effectiveTreeLayoutKey,
    modulePowerW,
}: UseDashboardDesignHandlersArgs) {
    const aiDesignCounterRef = useRef(0);

    const generateAIDesignId = useCallback(
        () => `ai-design-${Date.now()}-${++aiDesignCounterRef.current}`,
        [],
    );
    const generateSubScenarioId = useCallback(
        () => `tree-sub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        [],
    );

    const getLayoutLabel = useCallback(
        (layoutKey: string) => {
            if (layoutKey === 'default') return 'oneDRAW Default Maxfill';
            const combo = roofCombinationsFromAi.find((c: any) => c.name === layoutKey);
            return combo && /^(maximum\s+production|max\s+production(\s+with\s+best\s+roofs)?)$/i.test((combo.name || '').trim())
                ? 'Max Production with Best Roofs'
                : (combo?.name ?? layoutKey);
        },
        [roofCombinationsFromAi],
    );

    const getRoofFaceIds = useCallback(
        (layoutKey: string) => {
            if (layoutKey === 'default') return bestRoofsForLayout?.roofFaceIds ?? [];
            return roofCombinationsFromAi.find((c: any) => c.name === layoutKey)?.roofFaceIds ?? [];
        },
        [bestRoofsForLayout, roofCombinationsFromAi],
    );

    const handleWishlistDesign = useCallback(
        (
            key: string,
            categoryId: string,
            systemSizeKwOverride?: number | null,
            roofCountOverride?: number | null,
        ) => {
            const existing = savedAIDesigns.find((d) => d.layoutKey === key && d.categoryId === categoryId);
            if (existing) {
                removeAIDesign(existing.aiDesignId);
                return;
            }

            const roofFaceIds = getRoofFaceIds(key);
            const prod = layoutProductionByKey?.[key] ?? 0;
            const modules = layoutModulesByKey?.[key] ?? 0;
            const cachedScenarios = treeScenariosByLayoutKey?.[key] ?? [];
            const aiDesignId = generateAIDesignId();
            const systemKw = resolveWishlistSystemKw(modules, modulePowerW, systemSizeKwOverride);
            const roofCount =
                typeof roofCountOverride === 'number' && Number.isFinite(roofCountOverride) && roofCountOverride >= 0
                    ? roofCountOverride
                    : roofFaceIds.length;

            const layoutSubScenario: TreeCutSubScenario = {
                id: generateSubScenarioId(),
                parentDesignId: aiDesignId,
                scenarioIndex: 0,
                treeLabel: 'Layout',
                treeIds: [],
                cutCost: null,
                modules,
                roofCount,
                productionW: prod,
                systemKw,
                gainW: 0,
                isMultiTree: false,
            };

            const design: SavedAIDesign = {
                aiDesignId,
                layoutKey: key,
                label: getLayoutLabel(key),
                productionW: prod,
                systemKw,
                modules,
                roofCount,
                roofFaceIds,
                treeCount: treeCount ?? 0,
                createdAt: Date.now(),
                treeScenarios: cachedScenarios,
                treeCutSubScenarios: [layoutSubScenario],
                snapshotUrl: layoutSnapshotsByKey?.[key] ?? undefined,
                categoryId,
            };
            addAIDesign(design);
        },
        [
            savedAIDesigns,
            removeAIDesign,
            getRoofFaceIds,
            layoutProductionByKey,
            layoutModulesByKey,
            treeScenariosByLayoutKey,
            generateAIDesignId,
            generateSubScenarioId,
            getLayoutLabel,
            treeCount,
            layoutSnapshotsByKey,
            addAIDesign,
            modulePowerW,
        ],
    );

    const handleWishlistVariantDesign = useCallback(
        (
            categoryId: string,
            variant: {
                id?: string;
                label?: string;
                roofIds?: string[];
                productionW?: number;
                modules?: number;
                snapshotUrl?: string;
            },
        ) => {
            const normalizedCategory = categoryId || 'Other layout options';
            const roofIds = Array.isArray(variant.roofIds) ? variant.roofIds : [];
            const baseKey = variant.id || variant.label || 'variant';
            const layoutKey = `variant:${normalizedCategory}:${baseKey}`;
            const existing = savedAIDesigns.find(
                (d) => d.layoutKey === layoutKey && d.categoryId === normalizedCategory,
            );
            if (existing) {
                removeAIDesign(existing.aiDesignId);
                return;
            }

            const productionW =
                typeof variant.productionW === 'number' && Number.isFinite(variant.productionW)
                    ? variant.productionW
                    : 0;
            const modules =
                typeof variant.modules === 'number' && Number.isFinite(variant.modules)
                    ? variant.modules
                    : 0;
            const aiDesignId = generateAIDesignId();
            const systemKw = resolveWishlistSystemKw(modules, modulePowerW);

            const layoutSubScenario: TreeCutSubScenario = {
                id: generateSubScenarioId(),
                parentDesignId: aiDesignId,
                scenarioIndex: 0,
                treeLabel: 'Layout',
                treeIds: [],
                cutCost: null,
                modules,
                roofCount: roofIds.length,
                productionW,
                systemKw,
                gainW: 0,
                isMultiTree: false,
            };

            const design: SavedAIDesign = {
                aiDesignId,
                layoutKey,
                label: variant.label || 'Variant layout',
                categoryId: normalizedCategory,
                productionW,
                systemKw,
                modules,
                roofCount: roofIds.length,
                roofFaceIds: roofIds,
                treeCount: treeCount ?? 0,
                createdAt: Date.now(),
                treeScenarios: [],
                treeCutSubScenarios: [layoutSubScenario],
                snapshotUrl: variant.snapshotUrl ?? undefined,
            };
            addAIDesign(design);
        },
        [savedAIDesigns, removeAIDesign, generateAIDesignId, generateSubScenarioId, treeCount, addAIDesign, modulePowerW],
    );

    const getOrCreateDesignForLayoutKey = useCallback(
        (layoutKey: string): string => {
            const existing = savedAIDesigns.find((d) => d.layoutKey === layoutKey);
            if (existing) return existing.aiDesignId;

            const roofFaceIds = getRoofFaceIds(layoutKey);
            const prod = layoutProductionByKey?.[layoutKey] ?? 0;
            const modules = layoutModulesByKey?.[layoutKey] ?? 0;
            const newDesign: SavedAIDesign = {
                aiDesignId: generateAIDesignId(),
                layoutKey,
                label: getLayoutLabel(layoutKey),
                productionW: prod,
                systemKw: resolveWishlistSystemKw(modules, modulePowerW),
                modules,
                roofCount: roofFaceIds.length,
                roofFaceIds,
                treeCount: treeCount ?? 0,
                createdAt: Date.now(),
                treeScenarios: treeScenariosByLayoutKey?.[layoutKey] ?? [],
                treeCutSubScenarios: [],
                snapshotUrl: layoutSnapshotsByKey?.[layoutKey] ?? undefined,
            };
            addAIDesign(newDesign);
            return newDesign.aiDesignId;
        },
        [
            savedAIDesigns,
            getRoofFaceIds,
            layoutProductionByKey,
            layoutModulesByKey,
            generateAIDesignId,
            getLayoutLabel,
            treeCount,
            treeScenariosByLayoutKey,
            layoutSnapshotsByKey,
            addAIDesign,
            modulePowerW,
        ],
    );

    const handleSaveTreeCutAsSubScenario = useCallback(
        (parentDesignId: string, scenario: any, index: number) => {
            const parent = useOneButtonStore
                .getState()
                .savedAIDesigns.find((d) => d.aiDesignId === parentDesignId);
            if (!parent) return;
            const treeIds = scenario.treeIds?.length ? scenario.treeIds : (scenario.treeId ? [scenario.treeId] : []);
            const treeLabel =
                index === 0 && !scenario.treeId && !(treeIds?.length)
                    ? 'Ideal (no cuts)'
                    : (scenario.treeLabel ?? null);
            const snapshotUrl = (() => {
                const u = scenario?.snapshotUrl ?? scenario?.layoutSnapshotUrl;
                if (typeof u === 'string' && u.trim()) return u.trim();
                return undefined;
            })();
            const subModules =
                scenario.shadeWeights?.reduce((sum: number, row: any) => sum + (row.moduleCount ?? 0), 0) ??
                parent.modules;
            const sub: TreeCutSubScenario = {
                id: generateSubScenarioId(),
                parentDesignId,
                scenarioIndex: index,
                treeLabel,
                treeIds,
                cutCost: scenario.cutCost ?? null,
                modules: subModules,
                roofCount: parent.roofCount,
                productionW: scenario.actualWattage ?? 0,
                systemKw: resolveWishlistSystemKw(subModules, modulePowerW),
                gainW: (scenario.actualWattage ?? 0) - parent.productionW,
                isMultiTree: treeIds.length > 1,
                snapshotUrl,
            };
            addTreeCutSubScenario(parentDesignId, sub);
        },
        [generateSubScenarioId, addTreeCutSubScenario, modulePowerW],
    );

    const isTreeCutScenarioSavedForLayout = useCallback(
        (layoutKey: string, index: number): boolean => {
            const design = savedAIDesigns.find((d) => d.layoutKey === layoutKey);
            return design
                ? (design.treeCutSubScenarios ?? []).some((sub) => sub.scenarioIndex === index)
                : false;
        },
        [savedAIDesigns],
    );

    const isTreeCutScenarioSaved = useCallback(
        (index: number) => isTreeCutScenarioSavedForLayout(effectiveTreeLayoutKey, index),
        [effectiveTreeLayoutKey, isTreeCutScenarioSavedForLayout],
    );

    const handleSaveTreeCutScenario = useCallback(
        (scenario: any, index: number) => {
            if (isTreeCutScenarioSaved(index)) return;
            const parentId = getOrCreateDesignForLayoutKey(effectiveTreeLayoutKey);
            handleSaveTreeCutAsSubScenario(parentId, scenario, index);
        },
        [
            isTreeCutScenarioSaved,
            getOrCreateDesignForLayoutKey,
            effectiveTreeLayoutKey,
            handleSaveTreeCutAsSubScenario,
        ],
    );

    const handleSaveTreeCutScenarioWithLayoutKey = useCallback(
        (layoutKey: string, scenario: any, index: number) => {
            const parentId = getOrCreateDesignForLayoutKey(layoutKey);
            handleSaveTreeCutAsSubScenario(parentId, scenario, index);
        },
        [getOrCreateDesignForLayoutKey, handleSaveTreeCutAsSubScenario],
    );

    return {
        handleWishlistDesign,
        handleWishlistVariantDesign,
        handleSaveTreeCutScenario,
        handleSaveTreeCutScenarioWithLayoutKey,
        isTreeCutScenarioSaved,
        isTreeCutScenarioSavedForLayout,
    };
}
